import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Button, 
  Modal, 
  Form, 
  Input, 
  InputNumber, 
  DatePicker, 
  message, 
  Popconfirm, 
  Card,
  Row,
  Col,
  Statistic,
  Tag
} from 'antd';
import { 
  EditOutlined, 
  DeleteOutlined, 
  PlusOutlined, 
  PercentageOutlined,
  MoneyCollectOutlined 
} from '@ant-design/icons';
import { feeStructureApi } from '../services/api';
import { FeeStructure, CreateFeeStructure } from '../types';
import { useLanguage } from '../contexts/LanguageContext';
import dayjs from 'dayjs';

const { TextArea } = Input;

const SystemFeePage: React.FC = () => {
  const { t } = useLanguage();
  const [feeStructures, setFeeStructures] = useState<FeeStructure[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingFeeStructure, setEditingFeeStructure] = useState<FeeStructure | null>(null);
  const [form] = Form.useForm();

  // Fee type constants
  const TAX_RATE_TYPE = 'TAX_RATE';
  const DEFAULT_DRIVER_SALARY_RATE_TYPE = 'DEFAULT_DRIVER_SALARY_RATE';

  const fetchFeeStructures = async () => {
    setLoading(true);
    try {
      const response = await feeStructureApi.getAll();
      setFeeStructures(response.data);
    } catch (error) {
      console.error('Error fetching fee structures:', error);
      message.error(t('error.fetch.failed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeeStructures();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAdd = (feeType: string) => {
    setEditingFeeStructure(null);
    form.resetFields();
    form.setFieldsValue({ 
      feeType,
      affectedDate: dayjs()
    });
    setModalVisible(true);
  };

  const handleEdit = (feeStructure: FeeStructure) => {
    setEditingFeeStructure(feeStructure);
    form.setFieldsValue({
      ...feeStructure,
      affectedDate: dayjs(feeStructure.affectedDate)
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await feeStructureApi.delete(id);
      message.success(t('success.deleted'));
      fetchFeeStructures();
    } catch (error) {
      console.error('Error deleting fee structure:', error);
      message.error(t('error.delete.failed'));
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      const data: CreateFeeStructure = {
        ...values,
        affectedDate: values.affectedDate.format('YYYY-MM-DD')
      };

      if (editingFeeStructure) {
        await feeStructureApi.update(editingFeeStructure.id, data);
        message.success(t('success.updated'));
      } else {
        await feeStructureApi.create(data);
        message.success(t('success.created'));
      }

      setModalVisible(false);
      form.resetFields();
      fetchFeeStructures();
    } catch (error) {
      console.error('Error saving fee structure:', error);
      message.error(t('error.save.failed'));
    }
  };

  const getFeeTypeLabel = (feeType: string) => {
    switch (feeType) {
      case TAX_RATE_TYPE:
        return t('tax.rate');
      case DEFAULT_DRIVER_SALARY_RATE_TYPE:
        return t('default.driver.salary.rate');
      default:
        return feeType;
    }
  };

  const getFeeTypeColor = (feeType: string) => {
    switch (feeType) {
      case TAX_RATE_TYPE:
        return 'orange';
      case DEFAULT_DRIVER_SALARY_RATE_TYPE:
        return 'blue';
      default:
        return 'default';
    }
  };

  const formatRate = (rate: number) => {
    return `${(rate * 100).toFixed(2)}%`;
  };

  // Get current active rates for display
  const currentTaxRate = feeStructures
    .filter(f => f.feeType === TAX_RATE_TYPE && f.isActive)
    .sort((a, b) => new Date(b.affectedDate).getTime() - new Date(a.affectedDate).getTime())[0];

  const currentDriverRate = feeStructures
    .filter(f => f.feeType === DEFAULT_DRIVER_SALARY_RATE_TYPE && f.isActive)
    .sort((a, b) => new Date(b.affectedDate).getTime() - new Date(a.affectedDate).getTime())[0];

  const columns = [
    {
      title: t('fee.type'),
      dataIndex: 'feeType',
      key: 'feeType',
      render: (feeType: string) => (
        <Tag color={getFeeTypeColor(feeType)}>
          {getFeeTypeLabel(feeType)}
        </Tag>
      ),
    },
    {
      title: t('rate'),
      dataIndex: 'rate',
      key: 'rate',
      render: (rate: number) => formatRate(rate),
    },
    {
      title: t('description'),
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: t('affected.date'),
      dataIndex: 'affectedDate',
      key: 'affectedDate',
      render: (date: string) => dayjs(date).format('YYYY-MM-DD'),
    },
    {
      title: t('status'),
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? t('active') : t('inactive')}
        </Tag>
      ),
    },
    {
      title: t('actions'),
      key: 'actions',
      render: (_: any, record: FeeStructure) => (
        <div>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Popconfirm
            title={t('confirm.delete')}
            onConfirm={() => handleDelete(record.id)}
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div>
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={12}>
          <Card>
            <Statistic
              title={t('current.tax.rate')}
              value={currentTaxRate ? formatRate(currentTaxRate.rate) : 'N/A'}
              prefix={<PercentageOutlined />}
              valueStyle={{ color: '#f5222d' }}
            />
            <Button
              type="primary"
              size="small"
              icon={<PlusOutlined />}
              onClick={() => handleAdd(TAX_RATE_TYPE)}
              style={{ marginTop: 8 }}
            >
              {t('update.tax.rate')}
            </Button>
          </Card>
        </Col>
        <Col span={12}>
          <Card>
            <Statistic
              title={t('current.default.driver.salary.rate')}
              value={currentDriverRate ? formatRate(currentDriverRate.rate) : 'N/A'}
              prefix={<MoneyCollectOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
            <Button
              type="primary"
              size="small"
              icon={<PlusOutlined />}
              onClick={() => handleAdd(DEFAULT_DRIVER_SALARY_RATE_TYPE)}
              style={{ marginTop: 8 }}
            >
              {t('update.driver.rate')}
            </Button>
          </Card>
        </Col>
      </Row>

      <Card title={t('fee.structure.history')}>
        <Table
          columns={columns}
          dataSource={feeStructures}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
          }}
        />
      </Card>

      <Modal
        title={editingFeeStructure ? t('edit.fee.structure') : t('add.fee.structure')}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="feeType"
            label={t('fee.type')}
            rules={[{ required: true, message: t('required.field') }]}
          >
            <Input disabled />
          </Form.Item>

          <Form.Item
            name="rate"
            label={t('rate.percentage')}
            rules={[
              { required: true, message: t('required.field') },
              { type: 'number', min: 0, max: 1, message: t('rate.validation.message') }
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder="0.045 (for 4.5%)"
              step={0.001}
              precision={6}
              addonAfter="%"
              formatter={(value) => value ? `${(Number(value) * 100).toFixed(2)}` : ''}
              parser={(value) => Number((Number(value) / 100).toFixed(6))}
            />
          </Form.Item>

          <Form.Item
            name="description"
            label={t('description')}
          >
            <TextArea rows={3} placeholder={t('enter.description')} />
          </Form.Item>

          <Form.Item
            name="affectedDate"
            label={t('affected.date')}
            rules={[{ required: true, message: t('required.field') }]}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" style={{ marginRight: 8 }}>
              {editingFeeStructure ? t('update') : t('create')}
            </Button>
            <Button onClick={() => setModalVisible(false)}>
              {t('cancel')}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SystemFeePage;